import { Box, Chip, Stack, Tooltip } from '@mui/material';
import { FunctionComponent, useMemo, useState } from 'react';
import { useTranslator } from 'translation/useTranslator';
import { FeatureFlag, FeatureFlagId } from '../../../../@types/featureFlags';
import { FeatureFlagOrganizationDto } from '../../../../@types/organization';
import { SelectItem } from 'components/util/select/selectData';
import { Text } from 'components/text/Text';
import { EditFeatureFlagDialog } from './EditFeatureFlagDialog';
import AccessGuard, { rules, useAccessControl } from 'access';
import CustomSwitch from 'components/util/CustomSwitch';
import { MIconButton } from 'components/@material-extend';
import { IconEdit } from '@nps.today/component-library';
import useDisplayMessage from 'components/messages/useMessage';

const MAX_VISIBLE_ORGANIZATIONS = 4;

interface FeatureFlagRowProps {
  featureFlag: FeatureFlag;
  organizations: FeatureFlagOrganizationDto[];
  updateFeatureFlag: (flag: FeatureFlag) => Promise<void>;
}

export const FeatureFlagRow: FunctionComponent<FeatureFlagRowProps> = ({
  featureFlag,
  organizations,
  updateFeatureFlag
}) => {
  const translator = useTranslator();
  const displayMessage = useDisplayMessage();
  const { canEdit } = useAccessControl(rules.featureFlag.manage);
  const [editOpen, setEditOpen] = useState(false);
  const [toggling, setToggling] = useState(false);

  const selectedOrganizations = useMemo<SelectItem[]>(
    () =>
      organizations
        .filter((org) => featureFlag.organizations?.includes(org.id))
        .map((org) => ({ value: org.id, displayValue: org.name })),
    [organizations, featureFlag.organizations]
  );

  const isEnabledForEveryone = featureFlag.enabled && featureFlag.enabledForEveryone;

  const handleSave = async (flag: FeatureFlag) => {
    await updateFeatureFlag(flag);
    setEditOpen(false);
  };

  const handleToggleEnabled = async (enabled: boolean) => {
    setToggling(true);
    try {
      await updateFeatureFlag({ ...featureFlag, enabled });
      displayMessage.success({
        message: {
          title: enabled ? translator.featureFlagEnabled() : translator.featureFlagDisabled()
        }
      });
    } catch {
      displayMessage.error({ message: { title: translator.couldNotSave() } });
    } finally {
      setToggling(false);
    }
  };

  return (
    <>
      <Box
        data-testid={`feature-flag-row-${FeatureFlagId[featureFlag.id]}`}
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          transition: (theme) => theme.transitions.create(['border-color', 'box-shadow']),
          '&:hover': { borderColor: 'primary.main', boxShadow: 3 }
        }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
          <Stack spacing={1} sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  flexShrink: 0,
                  bgcolor: featureFlag.enabled ? 'success.main' : 'text.disabled'
                }}
              />
              <Text variant="subtitle1" sx={{ fontWeight: 600 }}>
                {FeatureFlagId[featureFlag.id]}
              </Text>
              {isEnabledForEveryone && (
                <Chip size="small" color="primary" label={translator.enabledForEveryone()} />
              )}
            </Box>
            {featureFlag.description && (
              <Text variant="body2" sx={{ color: 'text.secondary' }}>
                {featureFlag.description}
              </Text>
            )}
            <OrganizationTargeting
              isEnabledForEveryone={isEnabledForEveryone}
              organizations={selectedOrganizations}
            />
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
            <CustomSwitch
              switchProps={{
                checked: featureFlag.enabled,
                disabled: !canEdit || toggling,
                onChange: (_, checked) => handleToggleEnabled(checked),
                inputProps: { 'aria-label': translator.enabled() }
              }}
            />
            <AccessGuard rule={rules.featureFlag.manage}>
              <MIconButton
                onClick={() => setEditOpen(true)}
                aria-label={translator.edit()}
                data-testid="feature-flag-edit-button"
              >
                <IconEdit />
              </MIconButton>
            </AccessGuard>
          </Stack>
        </Stack>
      </Box>
      <EditFeatureFlagDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSave={handleSave}
        organizations={organizations}
        selectedFeatureFlag={featureFlag}
      />
    </>
  );
};

interface OrganizationTargetingProps {
  isEnabledForEveryone: boolean;
  organizations: SelectItem[];
}

const OrganizationTargeting: FunctionComponent<OrganizationTargetingProps> = ({
  isEnabledForEveryone,
  organizations
}) => {
  const translator = useTranslator();
  const visible = organizations.slice(0, MAX_VISIBLE_ORGANIZATIONS);
  const hidden = organizations.slice(MAX_VISIBLE_ORGANIZATIONS);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', pt: 0.5 }}>
      <Text
        variant="caption"
        sx={{
          color: 'text.disabled',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          mr: 0.5
        }}
      >
        {translator.organizationAccess()}
      </Text>
      {isEnabledForEveryone && (
        <Chip
          size="small"
          variant="outlined"
          color="primary"
          label={translator.enabledForAllOrganizations()}
        />
      )}
      {!isEnabledForEveryone &&
        visible.map((organization) => (
          <Tooltip title={organization.value} key={organization.value}>
            <Chip size="small" variant="outlined" label={organization.displayValue} />
          </Tooltip>
        ))}
      {!isEnabledForEveryone && hidden.length > 0 && (
        <Tooltip title={hidden.map((organization) => organization.displayValue).join(', ')}>
          <Chip size="small" label={`+${hidden.length}`} />
        </Tooltip>
      )}
      {!isEnabledForEveryone && organizations.length === 0 && (
        <Text variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          {translator.noAccessAssigned()}
        </Text>
      )}
    </Box>
  );
};
